import { Box, Select, Option, Typography, Button, IconButton, Stack, Input } from "@mui/joy";
import { useEffect, useState, useRef } from "react";
import { debounce } from "lodash";
import { Sun, Plus, X, Maximize2, Shield, Hash } from "lucide-react";

interface ISkylightList {
    value: string | null;
    onChange: (value: string) => void;
    loading?: boolean;
}

interface SkylightEntry {
    type: string;
    size: string;
    flashingKit: string;
    count: number;
}

/**
 * Repeatable list for multiple skylights.
 * Stores as JSON array: [{"type":"Dome Fixed","size":"Medium","flashingKit":"Dome Kit","count":1}]
 */
export function SkylightList(props: ISkylightList) {
    const { value, onChange, loading } = props;

    const parseValue = (val: string | null): SkylightEntry[] => {
        if (!val) return [{ type: "None", size: "None", flashingKit: "N/A", count: 1 }];
        try {
            const parsed = JSON.parse(val);
            if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        } catch {
            // Basic fallback
        }
        return [{ type: "None", size: "None", flashingKit: "N/A", count: 1 }];
    };

    const [entries, setEntries] = useState<SkylightEntry[]>(() => parseValue(value));

    const debouncedOnChange = useRef(
        debounce((updatedEntries: SkylightEntry[]) => {
            onChange(JSON.stringify(updatedEntries));
        }, 500)
    ).current;

    useEffect(() => {
        return () => { debouncedOnChange.cancel(); };
    }, [debouncedOnChange]);

    useEffect(() => {
        if (value) {
            setEntries(parseValue(value));
        }
    }, [value]);

    const handleUpdate = (index: number, field: keyof SkylightEntry, val: any) => {
        if (loading) return;
        const updated = entries.map((entry, i) => (i === index ? { ...entry, [field]: val } : entry));
        setEntries(updated);
        debouncedOnChange(updated);
    };

    const addEntry = () => {
        setEntries((prev) => [...prev, { type: "Flat Fixed", size: "Medium (6.6-9 sf)", flashingKit: "Step Flashing Kit", count: 1 }]);
    };

    const removeEntry = (index: number) => {
        if (entries.length <= 1) {
            const reset = [{ type: "None", size: "None", flashingKit: "N/A", count: 0 }];
            setEntries(reset);
            debouncedOnChange(reset);
            return;
        }
        const updated = entries.filter((_, i) => i !== index);
        setEntries(updated);
        debouncedOnChange(updated);
    };

    return (
        <Box>
            <Typography level="body-sm" sx={{ fontWeight: "md", mb: 1 }}>
                Skylights
            </Typography>
            <Stack spacing={2}>
                {entries.map((entry, i) => (
                    <Box key={i} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 'sm', bgcolor: 'background.surface', position: 'relative' }}>
                        <IconButton
                            size="sm"
                            variant="plain"
                            color="neutral"
                            onClick={() => removeEntry(i)}
                            sx={{ position: 'absolute', top: 4, right: 4 }}
                        >
                            <X size={16} />
                        </IconButton>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                            <Box sx={{ flex: '1 1 150px' }}>
                                <Typography level="body-xs" sx={{ mb: 0.5 }}>Type</Typography>
                                <Select
                                    size="sm"
                                    value={entry.type}
                                    onChange={(_e, val) => handleUpdate(i, "type", val)}
                                    startDecorator={<Sun size={14} />}
                                >
                                    <Option value="None">None</Option>
                                    <Option value="Dome Fixed">Dome Fixed</Option>
                                    <Option value="Flat Fixed">Flat Fixed</Option>
                                    <Option value="Roof Window">Roof Window</Option>
                                </Select>
                            </Box>
                            <Box sx={{ flex: '1 1 150px' }}>
                                <Typography level="body-xs" sx={{ mb: 0.5 }}>Size</Typography>
                                <Select
                                    size="sm"
                                    value={entry.size}
                                    onChange={(_e, val) => handleUpdate(i, "size", val)}
                                    startDecorator={<Maximize2 size={14} />}
                                >
                                    <Option value="None">None</Option>
                                    <Option value="Small (<6.6 sf)">Small (&lt;6.6 sf)</Option>
                                    <Option value="Medium (6.6-9 sf)">Medium (6.6–9 sf)</Option>
                                    <Option value="Large (9.1-10 sf)">Large (9.1–10 sf)</Option>
                                </Select>
                            </Box>
                            <Box sx={{ flex: '1 1 150px' }}>
                                <Typography level="body-xs" sx={{ mb: 0.5 }}>Flashing Kit</Typography>
                                <Select
                                    size="sm"
                                    value={entry.flashingKit}
                                    onChange={(_e, val) => handleUpdate(i, "flashingKit", val)}
                                    startDecorator={<Shield size={14} />}
                                >
                                    <Option value="Dome Kit">Dome Kit</Option>
                                    <Option value="Step Flashing Kit">Step Flashing Kit</Option>
                                    <Option value="N/A">N/A</Option>
                                </Select>
                            </Box>
                            <Box sx={{ flex: '0 1 80px' }}>
                                <Typography level="body-xs" sx={{ mb: 0.5 }}>Count</Typography>
                                <Input
                                    size="sm"
                                    type="number"
                                    value={entry.count}
                                    onChange={(e) => handleUpdate(i, "count", Number(e.target.value))}
                                    startDecorator={<Hash size={14} />}
                                />
                            </Box>
                        </Box>
                    </Box>
                ))}
                <Button
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    startDecorator={<Plus size={16} />}
                    onClick={addEntry}
                    sx={{ alignSelf: 'flex-start' }}
                >
                    Add Skylight
                </Button>
            </Stack>
        </Box>
    );
}
